import React from 'react';
import {Line, Legend, YAxis, XAxis, Label, Bar, LabelList, Tooltip, Text} from 'recharts';
import theme from '../../../constants/theme';
import {parseType} from './parse';
import {getBarColor, isLabelVisible, isDataNotEmpty, isYAxisNotEmpty} from './helper';
import {TooltipWrapper, TooltipItem, LabelUl, LabelLi} from './components';

const renderSvgText = ({x, y, dy = 0, height, width, fill, value}) => (
  <Text x={x} y={y} dy={dy} height={height} width={width} fill={fill} textAnchor="middle">
    {value}
  </Text>
);

const renderYAxis = (data, yAxisId, labelPosition, angle) => {
  if (!isYAxisNotEmpty(data, yAxisId)) return null;
  return (
    <YAxis yAxisId={yAxisId} orientation={yAxisId}>
      <Label
        value={
          data.sets[yAxisId].yLabel.title
            ? `${data.sets[yAxisId].yLabel.title} ${
                data.sets[yAxisId].yLabel.unit ? `[${data.sets[yAxisId].yLabel.unit}]` : null
              }`
            : ''
        }
        position={labelPosition}
        angle={angle}
      />
    </YAxis>
  );
};

const renderXAxisLabel = (data, yAxisId, position) =>
  data && data.sets && data.sets[yAxisId] && data.sets[yAxisId].xLabel && data.sets[yAxisId].xLabel.title ? (
    <Label
      value={`${data.sets[yAxisId].xLabel.title} ${
        data.sets[yAxisId].xLabel.unit ? `[${data.sets[yAxisId].xLabel.unit}]` : ''
      }`}
      position={position}
    />
  ) : null;

const renderXAxis = (data, showLabels) => (
  <XAxis
    xAxisId={'bottom'}
    dataKey="sets_xLabel_label"
    interval={0}
    height={showLabels ? 80 : 0}
    textAnchor="middle"
    tick={(t) => (
      <Text
        x={t.x}
        y={t.y}
        height={t.height}
        width={t.width / t.visibleTicksCount}
        fill={t.fill}
        textAnchor={t.textAnchor}
        verticalAnchor={t.verticalAnchor}>
        {t.payload.value}
      </Text>
    )}>
    {showLabels && renderXAxisLabel(data, 'left', 'insideBottomLeft')}
    {showLabels && renderXAxisLabel(data, 'right', 'insideBottomRight')}
  </XAxis>
);

const renderTooltipContent = ({payload, label}) => {
  if (payload && payload.length === 0) return null;
  return (
    <TooltipWrapper>
      <TooltipItem bold>{label}</TooltipItem>
      {payload &&
        payload.reverse().map((entry, index) => (
          <TooltipItem color={entry.color} key={index}>
            {entry.name}: {entry.value} {entry.unit}
          </TooltipItem>
        ))}
    </TooltipWrapper>
  );
};

const renderTooltip = () => <Tooltip content={renderTooltipContent} />;

const renderLegendContent = ({payload}) =>
  payload && (
    <LabelUl>
      {payload.map(
        (entry, index) =>
          entry.type === 'line' &&
          entry.payload.showLegend && (
            <LabelLi color={entry.color} key={index}>
              {entry.value}
            </LabelLi>
          )
      )}
    </LabelUl>
  );

const renderLegend = (showLabels) => (showLabels ? <Legend content={renderLegendContent} /> : null);

const renderLine = (data, parsedData, rowIndex, yAxisId, showLabels) => (
  <Line
    key={`${rowIndex}-${yAxisId}`}
    connectNulls
    yAxisId={yAxisId}
    xAxisId={'bottom'}
    isAnimationActive={false}
    unit={data.sets[yAxisId].yLabel.unit}
    type={parseType(data.sets[yAxisId].data[rowIndex].appearance.type)}
    label={(t) => {
      return showLabels && parsedData[t.index][`sets_${yAxisId}_data_${rowIndex}_points_showLabel`]
        ? renderSvgText({
            x: t.x,
            y: t.y,
            width: t.width,
            dy: -10,
            fill: theme.black,
            value: parsedData[t.index][`sets_${yAxisId}_data_${rowIndex}_points_labelValue`]
              ? parsedData[t.index][`sets_${yAxisId}_data_${rowIndex}_points_labelValue`]
              : t.value,
          })
        : null;
    }}
    stroke={data.sets[yAxisId].data[rowIndex].appearance.color}
    strokeDasharray={data.sets[yAxisId].data[rowIndex].appearance.dashArray}
    dataKey={`sets_${yAxisId}_data_${rowIndex}_points_value`}
    showLegend={!(!data.sets[yAxisId].data[rowIndex].label && data.sets[yAxisId].data.length === 1)}
    name={
      data.sets[yAxisId].data[rowIndex].label
        ? data.sets[yAxisId].data[rowIndex].label
        : data.sets[yAxisId].data.length === 1
        ? data.sets[yAxisId].yLabel.title
        : ' '
    }
  />
);

const renderBar = (data, parsedData, rowIndex, yAxisId, barIndex, showLabels) => (
  <Bar
    key={`${rowIndex}-${barIndex}-${yAxisId}`}
    barSize={1000}
    barGap={0}
    yAxisId={yAxisId}
    xAxisId={'bottom'}
    isAnimationActive={false}
    label={(t) =>
      showLabels && data.sets[yAxisId].data[rowIndex].bars[barIndex].label && isLabelVisible(t.height)
        ? renderSvgText({
            x: t.x + t.width / 2,
            y: t.y + t.height / 1.6,
            fill: theme.white,
            height: t.height,
            width: t.width,
            value: data.sets[yAxisId].data[rowIndex].bars[barIndex].label,
          })
        : null
    }
    name={
      data.sets[yAxisId].data[rowIndex].bars.length > 1
        ? data.sets[yAxisId].data[rowIndex].bars[barIndex].label
        : data.sets[yAxisId].xLabel.labels[rowIndex]
    }
    unit={data.sets[yAxisId].yLabel.unit}
    stackId={
      Array.isArray(parsedData[rowIndex][`sets_${yAxisId}_data_${rowIndex}_bars_${barIndex}_value`]) ? null : 'stacked'
    }
    dataKey={`sets_${yAxisId}_data_${rowIndex}_bars_${barIndex}_value`}
    fill={getBarColor(
      parsedData[rowIndex][`sets_${yAxisId}_data_${rowIndex}_bars_${barIndex}_appearance_color`],
      barIndex
    )}>
    {showLabels && barIndex === data.sets[yAxisId].data[rowIndex].bars.length - 1 ? (
      <LabelList content={(t) => parsedData[rowIndex][`sets_${yAxisId}_data_${t.index}_label`]} position={'top'} />
    ) : null}
  </Bar>
);

const barType = {
  ChartReportStack: (data, parsedData, rowData, rowIndex, yAxisId, showLabels) =>
    rowData.bars.map((bData, bIndex) => renderBar(data, parsedData, rowIndex, yAxisId, bIndex, showLabels)),
  ChartReportLine: (data, parsedData, rowData, rowIndex, yAxisId, showLabels) =>
    renderLine(data, parsedData, rowIndex, yAxisId, showLabels),
};

const getType = (__typename, data, parsedData, rowData, rowIndex, yAxisId, showLabels) =>
  barType[__typename](data, parsedData, rowData, rowIndex, yAxisId, showLabels);

const renderChart = (data, parsedData, yAxisId, showLabels) => {
  if (!isDataNotEmpty(data, yAxisId)) return null;
  return data.sets[yAxisId].data.map((rowData, rowIndex) =>
    getType(rowData.__typename, data, parsedData, rowData, rowIndex, yAxisId, showLabels)
  );
};

export {renderTooltip, renderLegend, renderXAxis, renderYAxis, renderChart};
